import React, { useState } from "react";
import {
  FiPause,
  FiPlay,
  FiEdit2,
  FiEye,
  FiTrash2,
  FiMail,
} from "react-icons/fi";
import { Workflow } from "../../types/workflow";
import WorkflowInfoGrid from "./WorkflowInfoGrid";
import { formatNextSendDate } from "../../utils/dateFormatters";

// Type-safe icon wrappers
const PauseIcon = FiPause as React.FC<React.SVGProps<SVGSVGElement>>;
const PlayIcon = FiPlay as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = FiEdit2 as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeIcon = FiEye as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const MailIcon = FiMail as React.FC<React.SVGProps<SVGSVGElement>>;

interface ScheduledWorkflowCardProps {
  workflow: Workflow;
  recipientCount?: number;
  onPauseToggle?: (workflowId: string, currentlyPaused: boolean) => Promise<void>;
  onPauseResume?: () => Promise<void>;
  onEdit: (workflow: Workflow) => void;
  onView: (workflow: Workflow) => void;
  onDelete: (workflow: Workflow) => void;
  onViewEmails?: (workflow: Workflow) => void;
  isToggling?: boolean;
}

const ScheduledWorkflowCard: React.FC<ScheduledWorkflowCardProps> = ({
  workflow,
  recipientCount,
  onPauseToggle,
  onPauseResume,
  onEdit,
  onView,
  onDelete,
  onViewEmails,
  isToggling = false,
}) => {
  const [isPausing, setIsPausing] = useState(false);
  const isPaused = !workflow.schedule_enabled;
  
  // Calculate recipient count from workflow if not provided
  // Use type assertion for runtime properties that may exist
  const workflowAny = workflow as any;
  const actualRecipientCount = recipientCount ?? (
    workflowAny.recipient_count || 
    workflowAny.selected_recipients?.length ||
    workflowAny.config?.recipientIds?.length ||
    0
  );

  // Get frequency from schedule_config
  const getFrequency = (): string => {
    const config = workflow.schedule_config;
    if (!config) return "Manual";
    
    if (config.frequency) {
      return config.frequency.charAt(0).toUpperCase() + config.frequency.slice(1);
    }
    
    // Try to infer from cron or other config
    if (config.cron) {
      if (config.cron.includes("0 0 1 * *")) return "Monthly";
      if (config.cron.includes("0 0 * * 0")) return "Weekly";
      if (config.cron.includes("0 0 * * *")) return "Daily";
    }
    
    return "Scheduled";
  };

  // Get integrations from workflow (capitalized)
  const getIntegrations = (): string[] => {
    if (workflow.selected_integrations && Array.isArray(workflow.selected_integrations)) {
      return workflow.selected_integrations.map((i: any) => {
        const name = typeof i === 'string' ? i : (i.provider || i.name || 'Unknown');
        // Capitalize first letter
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
      });
    }
    return [];
  };

  // Get insight types from workflow
  const getInsightTypes = (): string => {
    if (workflow.selected_insights && Array.isArray(workflow.selected_insights)) {
      const types = workflow.selected_insights.map((i: any) => 
        typeof i === 'string' ? i : (i.title || i.type || i.category || '')
      ).filter(Boolean);
      return types.join(", ") || "—";
    }
    if (workflow.workflow_type) {
      return workflow.workflow_type;
    }
    return "—";
  };

  // Format next execution date
  const formatNextSend = (): string => {
    if (!workflow.next_execution) return "Not scheduled";
    return formatNextSendDate(workflow.next_execution);
  };

  const handlePauseToggle = async () => {
    if (onPauseResume) {
      // New style - parent handles loading state
      await onPauseResume();
    } else if (onPauseToggle) {
      // Old style - this component handles loading state
      setIsPausing(true);
      try {
        await onPauseToggle(workflow.id, isPaused);
      } finally {
        setIsPausing(false);
      }
    }
  };
  
  // Use external loading state if provided, otherwise use internal
  const showLoading = isToggling || isPausing;

  const integrations = getIntegrations();

  return (
    <div className="su-workflow-card">
      {/* Row 1: Title + Badges + Actions */}
      <div className="su-workflow-card-header">
        <div className="su-workflow-card-title-row">
          <h3 className="su-workflow-card-title">{workflow.name}</h3>
          
          {/* Reconstructed Badges using Tailwind */}
          <div className="flex items-center gap-2">
            <span 
              className={`flex items-center justify-center px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider leading-none ${
                isPaused 
                  ? "bg-[#422006] text-[#facc15]" // Dark Amber bg, Yellow text for PAUSED
                  : "bg-[#14532d] text-[#4ade80]" // Dark Green bg, Green text for ACTIVE
              }`}
            >
              {isPaused ? "PAUSED" : "ACTIVE"}
            </span>
            <span className="flex items-center justify-center px-3 py-1.5 bg-[#3b0764] text-[#d8b4fe] text-[11px] font-bold tracking-wider leading-none">
              {getFrequency()}
            </span>
          </div>
        </div>
        
        <div className="su-workflow-actions">
          <button
            className="su-workflow-action-btn"
            onClick={handlePauseToggle}
            disabled={showLoading}
            title={isPaused ? "Resume workflow" : "Pause workflow"}
          >
            {showLoading ? (
              <div className="w-[18px] h-[18px] border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
            ) : isPaused ? (
              <PlayIcon className="w-[18px] h-[18px]" />
            ) : (
              <PauseIcon className="w-[18px] h-[18px]" />
            )}
          </button>
          <button
            className="su-workflow-action-btn"
            onClick={() => onEdit(workflow)}
            title="Edit workflow"
          >
            <EditIcon className="w-[18px] h-[18px]" />
          </button>
          <button
            className="su-workflow-action-btn"
            onClick={() => onView(workflow)}
            title="View workflow"
          >
            <EyeIcon className="w-[18px] h-[18px]" />
          </button>
          {onViewEmails && (
            <button
              className="su-workflow-action-btn"
              onClick={() => onViewEmails(workflow)}
              title="View sent emails"
            >
              <MailIcon className="w-[18px] h-[18px]" />
            </button>
          )}
          <button
            className="su-workflow-action-btn su-workflow-action-btn-delete"
            onClick={() => onDelete(workflow)}
            title="Delete workflow"
          >
            <TrashIcon className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>

      {/* Row 2: Info Grid - Using reusable component for consistent alignment */}
      <WorkflowInfoGrid
        columns={[
          {
            label: "Next Send",
            value: formatNextSend(),
          },
          {
            label: "Recipients",
            value: `${actualRecipientCount} contacts`,
          },
          {
            label: "Integrations",
            value: integrations.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {integrations.map((integration, idx) => (
                  <span key={idx} className="su-integration-badge">
                    {integration}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-white/50">—</span>
            ),
          },
          {
            label: "Insight Types",
            value: getInsightTypes(),
          },
        ]}
      />
    </div>
  );
};

export default ScheduledWorkflowCard;

